import type {
    CommunicationPatientRef,
    CommunicationStatus,
    CreateCommunicationInput
} from '../../../../services/communications';
import {
    buildCreateEntryInput,
    emptyEntryFormValues,
    entryFormValuesDiffer,
    type EntryFormValues
} from './entryFormValues';

// The create page's form model - thread fields plus the first entry's
// fields in one shape. Title is the only required field.
export interface CommunicationFormValues {
    // Immutable once set - never diffed, never sent again after create.
    referenceNo: string;
    title: string;
    status: CommunicationStatus;
    categories: string[];
    patients: CommunicationPatientRef[];
    assigneeEmail: string | null;
    entry: EntryFormValues;
}

export function buildInitialCommunicationFormValues(
    referenceNo: string,
    defaultContact: string | null
): CommunicationFormValues {
    return {
        referenceNo,
        title: '',
        status: 'open',
        categories: [],
        patients: [],
        assigneeEmail: null,
        entry: emptyEntryFormValues('call', defaultContact)
    };
}

/** Order-independent equality for a set of string ids/values. */
function sortedSetEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, i) => value === sortedB[i]);
}

function patientsEqual(a: CommunicationPatientRef[], b: CommunicationPatientRef[]): boolean {
    return sortedSetEqual(
        a.map((p) => p.id),
        b.map((p) => p.id)
    );
}

export function communicationFormValuesDiffer(
    a: CommunicationFormValues,
    b: CommunicationFormValues
): boolean {
    return (
        a.title.trim() !== b.title.trim() ||
        a.status !== b.status ||
        !sortedSetEqual(a.categories, b.categories) ||
        !patientsEqual(a.patients, b.patients) ||
        a.assigneeEmail !== b.assigneeEmail ||
        entryFormValuesDiffer(a.entry, b.entry)
    );
}

/** `practiceId` is omitted (not `null`) when unpicked - the backend treats
 *  a missing practiceId as "unassigned". */
export function buildCreateCommunicationInput(
    practiceId: string | null,
    current: CommunicationFormValues
): CreateCommunicationInput {
    return {
        ...(practiceId && { practiceId }),
        referenceNo: current.referenceNo,
        title: current.title.trim(),
        status: current.status,
        ...(current.categories.length > 0 && { categories: current.categories }),
        ...(current.patients.length > 0 && { patientIds: current.patients.map((p) => p.id) }),
        ...(current.assigneeEmail && { assigneeEmail: current.assigneeEmail }),
        entry: buildCreateEntryInput(current.entry)
    };
}
